import { Box, Divider, Flex, Stack, useColorModeValue as mode } from '@chakra-ui/react';
import DashboardCard from '@components/atoms/DashboardCard';
import Button, { ButtonVariants } from '@components/interactive/Button';
import { getPlanTypeCopy } from '@components/pages/account/aep/recommendations/utils';
import Body1 from '@components/typography/Body1';
import Header from '@components/typography/Header';
import { Routes } from '@constants/routes';
import { useAuthenticatedContextProvider } from '@contexts/AuthenticatedContextProvider';
import { PlanType } from '@enums/PlanType';
import medicalHistoryIcon from '@public/flaticons/medical-history.svg';
import pillsMedicineIcon from '@public/flaticons/pills-medicine.svg';
import CalendarIcon from '@public/flaticons/calendar.svg';
import Image from 'next/image';
import router from 'next/router';
import { formatInTimeZone } from 'date-fns-tz';

export const getTitle = (plan) => {
  let planText = '';
  if (plan.plan_type === PlanType.MAPD || plan.plan_type === PlanType.MA) {
    planText = 'Unhappy With Your Medicare Advantage Plan?';
  } else {
    planText = `Reshop Your Prescription Drug Plan`;
  }
  return planText;
};

export const getDescription = (plan) => {
  const isMa = plan.plan_type === PlanType.MA || plan.plan_type === PlanType.MAPD;
  if (isMa) {
    return 'If you are unhappy with your plan benefits in 2027, talk to one of our agents to find more suitable coverage.';
  }
  return "The Annual Enrollment Period (AEP) runs from 10/15 to 12/7. Find out if there's a 2027 plan that covers your prescription drug needs and is significantly lower cost than the one you're on.";
};

// Formats like "Jan 1, 2025 at 6:00 PM EDT"
const formatDatetime = (dateString: string, timeZone?: string) => {
  if (!dateString) return '';
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return formatInTimeZone(new Date(dateString), tz, "MMM d, yyyy 'at' h:mm a zzz");
};

function AEPDashboardCard({ plan }) {
  const isMA = plan.plan_type === PlanType.MA || plan.plan_type === PlanType.MAPD;
  const { authenticatedUser: user, reFetchUser } = useAuthenticatedContextProvider();

  const appointment = user?.appointment;
  const appointmentStart = appointment?.startTime || appointment?.event_start_time || appointment?.eventStartTime;
  const hasAppointment = !!appointment && !!appointmentStart;

  const title = getTitle(plan);
  const description = getDescription(plan);
  const aepIcon = isMA ? medicalHistoryIcon : pillsMedicineIcon;

  const formattedStart = hasAppointment ? formatDatetime(appointmentStart, appointment?.inviteeTimezone) : '';

  return (
    <DashboardCard id="aep-reshop-card">
      <Stack direction={['column', 'column', 'row']} alignItems={['stretch', 'stretch', 'normal']} w="100%">
        <Divider
          opacity={1}
          borderColor={mode('blue.500', 'blue.200')}
          borderWidth="2px"
          height="inherit"
          borderRadius={5}
          orientation="vertical"
          mr={[0, 0, 4]}
          ml={[0, 0, -5]}
          mb={[6, 6, 0]}
          flexDir="column"
        />
        <Box
          minW={['80px', '80px', '110px']}
          minH={['80px', '80px', '110px']}
          position="relative"
          display="flex"
          alignItems="center"
          mr={[0, 0, '16px !important']}
        >
          <Image src={hasAppointment ? CalendarIcon : aepIcon} alt="AEP enrollment icon" fill />
        </Box>
        <Stack pt={[6, 6, 0]}>
          <Header as="h4" mb={2} display="inline-block">
            {hasAppointment ? 'Your AEP Appointment' : title}
          </Header>
          {isMA && hasAppointment ? (
            <>
              <Body1 pb={3}>
                Your appointment is set with <span style={{ fontWeight: 'bold' }}>{appointment?.agentName}</span> on{' '}
                <span style={{ fontWeight: 'bold' }}>{formattedStart}</span>.
              </Body1>
              <Flex alignItems="center" direction={['column', 'column', 'row']} gap={4} w="100%">
                <Button
                  id="aep-reschedule"
                  href={appointment?.rescheduleUrl}
                  target="_blank"
                  width={['100%', '100%', 200]}
                >
                  Reschedule
                </Button>
                <Button
                  id="aep-cancel"
                  href={appointment?.cancelUrl}
                  target="_blank"
                  variant={ButtonVariants.secondary}
                  width={['100%', '100%', 200]}
                >
                  Cancel
                </Button>
              </Flex>
            </>
          ) : (
            <>
              <Body1 pb={3}>{description}</Body1>
              <Flex alignItems="center" direction={['column', 'column', 'row']} gap={4} w="100%">
                <Button
                  id="aep-get-started"
                  href={isMA ? Routes.accountSurveyAepLanding : Routes.aepIntro}
                  width={['100%', '100%', 200]}
                  onClick={() => {
                    reFetchUser();
                    router.push(isMA ? Routes.accountSurveyAepLanding : Routes.aepIntro);
                  }}
                >
                  {isMA ? 'Talk to an Agent' : 'Get Started'}
                </Button>
              </Flex>
            </>
          )}
        </Stack>
      </Stack>
    </DashboardCard>
  );
}

export default AEPDashboardCard;
